import { Dialog } from "@mui/material";

import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "@store/store";
import {
    DialogType,
    ValidateDialogProps,
    ValidateVisitDialogProps,
    CommentsDialogProps,
    AttribCodeDialogProps,
    NewMajorityShareholder,
    ExternalReviewComments,
    RegistrationInPortfolioErrors,
    EditionBankingInformationDialogProps,
    RuleModelEditionDialogProps,
    AdditionalAccountDialogProps,
} from "@interfaces/slices";

import { Validar } from "../DialogosSegmentos/Validar";
import { Comentarios } from "../DialogosSegmentos/Comentarios";
import { DocumentosFaltantes } from "../DialogosSegmentos/DocumentosFaltantes";
import { DescargarReportePld } from "../DialogosSegmentos/DescargarReportePld";
import { CodigoAtribucion } from "../DialogosSegmentos/CodigoAtribucion";
import { setDialogConfig } from "@store/slices/componentsSlice";
import { NuevoAccionistaMayoritario } from "../DialogosSegmentos/NuevoAccionistaMayoritario";
import { ObservacionesRevisionExterna } from "../DialogosSegmentos/ObservacionesRevisionExterna";
import { ErroresAltaCartera } from "../DialogosSegmentos/ErroresAltaCartera";
import { EdicionInfoBancaria } from "../DialogosSegmentos/EdicionInfoBancaria";
import { AltaEdicionReglaModelo } from "../DialogosSegmentos/AltaEdicionReglaModelo";
import { CuentaAdicional } from "../DialogosSegmentos/CuentaAdicional";

/**
 * V2 de `@components/DialogManager.tsx` — misma máquina de estados (Redux
 * `state.components.dialog` + `setDialogConfig`) y los mismos diálogos de
 * `DialogosSegmentos/*` (aún no migrados a v2 individualmente), pero montado
 * dentro de layouts v2 (`ThemeProvider theme={bpmV2Theme}`) para que cualquier
 * subFeature v2 que dispare `setDialogConfig(...)` tenga dónde renderizarse —
 * antes sólo estaba disponible en `BaseLayout.tsx`/`SolicitudLayout.tsx` (v1).
 * Los diálogos individuales se pueden ir rediseñando uno a uno más adelante,
 * igual que las secciones de cada subFeature.
 */
export const DialogManager = () => {
    const dispatch = useDispatch();

    const dialogConfig = useSelector((state: RootState) => state.components.dialog);

    const dialogs = new Map<DialogType, React.ReactNode>([
        [DialogType.NONE, <></>],
        [
            DialogType.VALIDAR_DOC,
            <Validar
                type={"VALIDATE_DOC"}
                title={(dialogConfig.props as ValidateDialogProps)?.title}
                isProcessing={dialogConfig?.isProcessing}
                onBackClick={dialogConfig.onRejectClick ?? (() => {})}
                onContinueClick={dialogConfig.onAcceptClick ?? (() => {})}
            />,
        ],
        [
            DialogType.VALIDAR_EXP,
            <Validar
                type={"VALIDATE_EXP"}
                title={(dialogConfig.props as ValidateDialogProps)?.title}
                isProcessing={dialogConfig?.isProcessing}
                onBackClick={dialogConfig.onRejectClick ?? (() => {})}
                onContinueClick={dialogConfig.onAcceptClick ?? (() => {})}
            />,
        ],
        [
            DialogType.VALIDAR_DIC,
            <Validar
                type={"COMPLETE"}
                title={(dialogConfig.props as ValidateDialogProps)?.title}
                isProcessing={dialogConfig?.isProcessing}
                onBackClick={dialogConfig.onRejectClick ?? (() => {})}
                onContinueClick={dialogConfig.onAcceptClick ?? (() => {})}
            />,
        ],
        [
            DialogType.AGENDAR_VISITA,
            <Validar
                type={"VISIT"}
                title={(dialogConfig.props as ValidateDialogProps)?.title}
                date={(dialogConfig.props as ValidateVisitDialogProps)?.date}
                time={(dialogConfig.props as ValidateVisitDialogProps)?.time}
                isProcessing={dialogConfig?.isProcessing}
                onBackClick={dialogConfig.onRejectClick ?? (() => {})}
                onContinueClick={dialogConfig.onAcceptClick ?? (() => {})}
            />,
        ],
        [
            DialogType.BORRAR_DOC,
            <Validar
                type={"DELETE_DOC"}
                title={(dialogConfig.props as ValidateDialogProps)?.title}
                isProcessing={dialogConfig?.isProcessing}
                onBackClick={dialogConfig.onRejectClick ?? (() => {})}
                onContinueClick={dialogConfig.onAcceptClick ?? (() => {})}
            />,
        ],
        [
            DialogType.DOCS_FALTANTES,
            <DocumentosFaltantes
                onBackClick={dialogConfig.onRejectClick ?? (() => {})}
                onConfirmClick={dialogConfig.onAcceptClick ?? (() => {})}
            />,
        ],
        [
            DialogType.DESCARGAR_REPORTE_PLD,
            <DescargarReportePld
            // title={(dialogConfig.props as StatusNotificationDialogProps)?.title}
            // iconType={(dialogConfig.props as StatusNotificationDialogProps)?.iconType}
            // onContinueClick={dialogConfig.onAcceptClick ?? (() => {})}
            />,
        ],
        [
            DialogType.COMENTARIOS,
            <Comentarios
                title={(dialogConfig.props as CommentsDialogProps)?.title}
                subtitle={(dialogConfig.props as CommentsDialogProps)?.subtitle}
                moduleCode={(dialogConfig.props as CommentsDialogProps)?.moduleCode}
                metadata={(dialogConfig.props as CommentsDialogProps)?.metadata}
                onBackClick={dialogConfig.onRejectClick ?? (() => {})}
                onConfirmClick={dialogConfig.onAcceptClick ?? (() => {})}
            />,
        ],
        [
            DialogType.CODIGOS_ATRIBUCION,
            <CodigoAtribucion
                {...(dialogConfig.props as AttribCodeDialogProps)}
                onBackClick={dialogConfig.onRejectClick ?? (() => dispatch(setDialogConfig()))}
                onConfirmClick={dialogConfig.onAcceptClick ?? (() => {})}
            />,
        ],
        [
            DialogType.NUEVO_ACCIONISTA_MAYORITARIO,
            <NuevoAccionistaMayoritario
                {...(dialogConfig.props as NewMajorityShareholder)}
                onConfirmClick={dialogConfig.onAcceptClick ?? (() => {})}
            />,
        ],
        [
            DialogType.OBSERVACION_REVISION_EXTERNA,
            <ObservacionesRevisionExterna
                {...(dialogConfig.props as ExternalReviewComments)}
                onConfirmClick={dialogConfig.onAcceptClick ?? (() => {})}
                onBackClick={dialogConfig.onRejectClick ?? (() => dispatch(setDialogConfig()))}
            />,
        ],
        [
            DialogType.ERRORES_ALTA_CARTERA,
            <ErroresAltaCartera {...(dialogConfig.props as RegistrationInPortfolioErrors)} />,
        ],
        [
            DialogType.EDICION_INFO_BANCARIA,
            <EdicionInfoBancaria {...(dialogConfig.props as EditionBankingInformationDialogProps)} />,
        ],
        [
            DialogType.ALTA_EDICION_REGLAS_MODELO,
            <AltaEdicionReglaModelo {...(dialogConfig.props as RuleModelEditionDialogProps)} />,
        ],
        [DialogType.CUENTA_ADICIONAL, <CuentaAdicional {...(dialogConfig.props as AdditionalAccountDialogProps)} />],
    ]);

    return (
        <Dialog
            open={dialogConfig.shown}
            fullScreen={dialogConfig.fullscreen}
            maxWidth={dialogConfig.maxWidth}
            fullWidth={dialogConfig.fullWidth}
        >
            {dialogs.get(dialogConfig.type)}
        </Dialog>
    );
};
